// hooks for managing diff lines and columns

import { useCallback, useMemo } from 'react';
import { ViewStyle } from 'react-native';
import { ProcessedLine, CollapsedSection as CollapsedSectionType, ChangeType, InlineDiffSegment, InlineChangeType, GroupPosition } from '@components/ncFileVersions/components/NCFileDiffView/types';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import { isPhone, isWeb } from '@helpers';
import { ColorFunction } from '@styles/colors/types';

export type DiffRenderGroup =
  | { type: 'lines'; key: string; items: ProcessedLine[] }
  | { type: 'collapsed'; key: string; section: CollapsedSectionType };

const isDiffableChange = (line?: ProcessedLine) => line && (line.changeType === ChangeType.ADDED || line.changeType === ChangeType.REMOVED);

const isPlaceholderLineContent = (line?: ProcessedLine) => line && line.content.trim().length === 0;

export const determineLineGroupPosition = (line: ProcessedLine, prev?: ProcessedLine, next?: ProcessedLine): GroupPosition => {
  if (!isDiffableChange(line)) {
    return undefined;
  }

  const isSameGroup = (neighbor?: ProcessedLine) => {
    if (!isDiffableChange(neighbor)) {
      return false;
    }
    if (isPlaceholderLineContent(line) || isPlaceholderLineContent(neighbor)) {
      return true;
    }
    return neighbor?.changeType === line.changeType;
  };

  const prevSame = isSameGroup(prev);
  const nextSame = isSameGroup(next);

  if (!prevSame && !nextSame) {
    return 'single';
  }
  if (!prevSame && nextSame) {
    return 'start';
  }
  if (prevSame && nextSame) {
    return 'middle';
  }
  if (prevSame && !nextSame) {
    return 'end';
  }
  return undefined;
};

interface InlineSegmentViewModel extends InlineDiffSegment {
  id: string;
  backgroundColor: string;
  textColor: string;
}

interface UseManageDiffLineParams {
  C: ColorFunction;
  line: ProcessedLine;
  isOldVersion: boolean;
  groupPosition?: GroupPosition;
  isFirstLineInDocument?: boolean;
}

interface UseManageDiffLineResult {
  isTablet: boolean;
  containerStyle: ViewStyle;
  firstLineTabletPadding?: ViewStyle;
  placeholderRowStyle?: ViewStyle;
  lineNumber: {
    display: string;
    color: string;
    hidden: boolean;
  };
  content: {
    color: string;
    opacity: number;
    value: string;
    placeholderGlyph: string;
    isPlaceholderLine: boolean;
    isPlaceholderValue: boolean;
  };
  inlineSegments: {
    hasSegments: boolean;
    segments: InlineSegmentViewModel[];
  };
}

const buildInlineSegmentViewModels = (
  segments: InlineDiffSegment[] | undefined,
  getDiffColor: (variant: 'Marked Character Right' | 'Marked Character Left') => string,
  textColor: string
): InlineSegmentViewModel[] => {
  if (!segments || !segments.length) {
    return [];
  }

  return segments.map((segment, index) => {
    const isWhitespaceSegment = segment.value.trim().length === 0;
    const backgroundColor = !isWhitespaceSegment
      ? segment.type === InlineChangeType.ADDED
        ? getDiffColor('Marked Character Right')
        : segment.type === InlineChangeType.REMOVED
        ? getDiffColor('Marked Character Left')
        : 'transparent'
      : 'transparent';

    return {
      ...segment,
      id: `${segment.type}-${index}`,
      backgroundColor,
      textColor: isWhitespaceSegment ? 'transparent' : textColor,
    };
  });
};

export const useManageDiffLine = ({
  C,
  line,
  isOldVersion,
  groupPosition,
  isFirstLineInDocument,
}: UseManageDiffLineParams): UseManageDiffLineResult => {
  const isTablet = !isPhone && !isWeb;
  const tabletRowHeight = useMemo(() => Math.round(scale(26, false)), []);
  const isAdded = line.changeType === ChangeType.ADDED;
  const isRemoved = line.changeType === ChangeType.REMOVED;
  const schemeKey = C.scheme === 'dark' ? 'Dark' : 'Light';

  const getDiffColor = useCallback(
    (variant: 'Marked Section Right' | 'Marked Section Left' | 'Placeholder' | 'Marked Character Right' | 'Marked Character Left') => C(`NC File Comparison/${schemeKey}/${variant}`),
    [C, schemeKey]
  );

  const isPlaceholderLine = line.content.trim().length === 0 && line.changeType !== ChangeType.UNCHANGED;

  const backgroundColor = useMemo(() => {
    if (isPlaceholderLine) {
      return getDiffColor('Placeholder');
    }
    if (isAdded) {
      return getDiffColor('Marked Section Right');
    }
    if (isRemoved) {
      return getDiffColor('Marked Section Left');
    }
    return 'transparent';
  }, [getDiffColor, isAdded, isRemoved, isPlaceholderLine]);

  const lineNumber = line.displayLineNumber ?? (isOldVersion ? line.oldLineNumber : line.newLineNumber);
  const isPlaceholderValue = lineNumber === null && !line.content;
  const lineNumberDisplay = lineNumber !== null ? lineNumber.toString() : isPlaceholderLine || isPlaceholderValue ? '--' : '';
  const lineNumberColor = C('Label/Light/Primary');

  const placeholderGlyph = isTablet ? '\u00A0' : ' ';

  const baseRadius = scale(18);
  const collapseTopForGroup = groupPosition && (groupPosition === 'middle' || groupPosition === 'end');
  const collapseBottomForGroup = groupPosition && (groupPosition === 'middle' || groupPosition === 'start');
  // Use unrounded scaling on tablets to prevent accumulated rounding errors
  const marginTop = collapseTopForGroup ? 0 : isTablet ? scale(2, false) : scale(2);
  const marginBottom = collapseBottomForGroup ? 0 : isTablet ? scale(2, false) : scale(2);

  const shouldRound = (position: GroupPosition | undefined, sides: GroupPosition[]) => Boolean(position && sides.includes(position));
  const borderRadiusStyle: ViewStyle = groupPosition
    ? {
        borderTopLeftRadius: shouldRound(groupPosition, ['single', 'start']) ? baseRadius : 0,
        borderTopRightRadius: shouldRound(groupPosition, ['single', 'start']) ? baseRadius : 0,
        borderBottomLeftRadius: shouldRound(groupPosition, ['single', 'end']) ? baseRadius : 0,
        borderBottomRightRadius: shouldRound(groupPosition, ['single', 'end']) ? baseRadius : 0,
      }
    : { borderRadius: baseRadius };

  const containerStyle: ViewStyle = {
    backgroundColor,
    marginTop,
    marginBottom,
    ...borderRadiusStyle,
  };

  const monoLineHeight = font['NC Diff Mono']?.lineHeight ?? scale(22);
  const placeholderRowStyle =
    isTablet && isPlaceholderLine
      ? {
          minHeight: tabletRowHeight,
        }
      : undefined;

  const firstLineTabletPadding = isTablet && isFirstLineInDocument ? { paddingTop: scale(8) } : undefined;

  const inlineSegmentViewModels = useMemo(
    () => buildInlineSegmentViewModels(line.inlineSegments, (variant) => getDiffColor(variant), C('Label/Light/Primary')),
    [line.inlineSegments, getDiffColor, C]
  );

  const contentColor = isPlaceholderLine ? 'transparent' : C('Label/Light/Primary');
  const contentOpacity = isPlaceholderValue ? 0.4 : 1;

  return {
    isTablet,
    containerStyle,
    firstLineTabletPadding,
    placeholderRowStyle,
    lineNumber: {
      display: lineNumberDisplay,
      color: lineNumberColor,
      hidden: Boolean(isPlaceholderLine),
    },
    content: {
      color: contentColor,
      opacity: contentOpacity,
      value: line.content,
      placeholderGlyph,
      isPlaceholderLine,
      isPlaceholderValue,
    },
    inlineSegments: {
      hasSegments: inlineSegmentViewModels.length > 0,
      segments: inlineSegmentViewModels,
    },
  };
};
